import { computed } from "vue";
import type { Ref, ComputedRef } from "vue";
import type { DatabaseSkill } from "~/typings/database.d";
import type { Portfolio } from "~/typings/portfolio";
import { useI18n } from "~/composables/useI18n";

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_YEAR = 365.25;

export interface SkillMetrics {
  yearsOfExperience: number;
  yearsDisplay: string;
  projectCount: number;
  relatedProjects: Portfolio.Experience[];
}

export type SkillWithMetrics = DatabaseSkill & SkillMetrics;

/**
 * Computes display metrics for a single skill.
 *
 * **Years of experience** — derived from the union of all date intervals
 * across experiences that used this skill, so that concurrent projects
 * (e.g. a freelance engagement running in parallel with a full-time job)
 * are not double-counted. Overlapping intervals are merged before summing.
 *
 * **Tier** (resolved separately via `getTier` in `useSkillCategories`):
 * - Expert     — `expertise >= 4`
 * - Proficient — `expertise >= 2`
 * - Familiar   — `expertise <= 1`
 */
export function computeSkillMetrics(
  skill: DatabaseSkill,
  experiences: Portfolio.Experience[],
  now = new Date()
): SkillMetrics {
  const relatedProjects = experiences.filter((exp) =>
    exp.technologies.some((tech) => tech.id === skill.id)
  );

  const projectCount = relatedProjects.length;

  // Build [start, end] intervals in ms, then merge overlaps so that
  // parallel projects are not double-counted.
  const intervals = relatedProjects.map((exp) => {
    const start = new Date(exp.startDate).getTime();
    const end = exp.endDate ? new Date(exp.endDate).getTime() : now.getTime();
    return [start, Math.max(start, end)] as [number, number];
  });

  intervals.sort((a, b) => a[0] - b[0]);

  const totalDays = intervals
    .reduce<[number, number][]>((merged, [start, end]) => {
      const last = merged[merged.length - 1];
      if (last && start <= last[1]) {
        last[1] = Math.max(last[1], end);
      } else {
        merged.push([start, end]);
      }
      return merged;
    }, [])
    .reduce((sum, [start, end]) => sum + (end - start) / MS_PER_DAY, 0);

  const yearsOfExperience = Math.floor(totalDays / DAYS_PER_YEAR);

  const t = useI18n();
  const yearsDisplay =
    yearsOfExperience < 1
      ? t("skillsPage.yearsDisplay.lessThanOneYear")
      : t("skillsPage.yearsDisplay.years")(yearsOfExperience);

  return { yearsOfExperience, yearsDisplay, projectCount, relatedProjects };
}

export function useSkillMetrics(
  skills: Ref<DatabaseSkill[]> | ComputedRef<DatabaseSkill[]>,
  experiences: Ref<Portfolio.Experience[]> | ComputedRef<Portfolio.Experience[]>
): ComputedRef<SkillWithMetrics[]> {
  return computed<SkillWithMetrics[]>(() =>
    skills.value.map((skill) => ({
      ...skill,
      ...computeSkillMetrics(skill, experiences.value),
    }))
  );
}
